import React, { useEffect, useState } from "react";
import { Box, Grid, Typography, Button, useMediaQuery, useTheme } from "@mui/material";
import KenLoader from "../../../GlobalComponents/KenLoader";
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import { useHistory } from "react-router-dom";
import routes from "../../../Utils/routes";
import storageService from '../../../Utils/storageService';
import startup from '../../../Assets/startup.svg'
import mentor from '../../../Assets/mentor.svg'
import jury from '../../../Assets/jury.svg'
import venue from '../../../Assets/venue.svg'
interface PortalApiResponse {
  "venue-booking"?: {
    User: {
      id: string;
      email: string;
    };
    access: boolean;
    status: string;
    roleType?: string;
    id: string;
  };
  jury?: {
    access: boolean;
    status: string;
    userId: string;
  };
  roles?: Array<{
    Id: string;
    PersonId__c: string;
    Status__c: string;
    Role_Type__c: string;
  }>;
}

interface PortalData {
  id: string;
  name: string;
  label: string;
  roleType: string | null;
  img: string;
}

const transformData = (apiData: PortalApiResponse): PortalData[] => {
  const transformed: PortalData[] = [];

  // Helper function to get image based on role name
  const getImageForRole = (roleName: string): string => {
    switch (roleName.toLowerCase()) {
      case 'startup':
        return startup;
      case 'mentor':
        return mentor;
      case 'venue':
        return venue;
      case 'jury':
      case 'panelist':
        return jury;
      default:
        return "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTiEnEPschHGJaMW26TTzXBOc_v7RjMakozdQ&s";
    }
  };

  // Track unique role types to avoid duplicates
  const uniqueRoleTypes = new Set<string>();
  
  // Check if Panelist role exists in roles array
  const hasPanelistRole = apiData.roles?.some(role => role.Role_Type__c === "Panelist");

  // Add roles data (always accessible) - filter duplicates
  if (apiData.roles && apiData.roles.length > 0) {
    apiData.roles.forEach((role) => {
      // Only add if this role type hasn't been added yet
      if (!uniqueRoleTypes.has(role.Role_Type__c)) {
        uniqueRoleTypes.add(role.Role_Type__c);
        transformed.push({
          id: role.Id,
          name: role.Role_Type__c,
          label: role.Role_Type__c,
          roleType: role.Role_Type__c,
          img: getImageForRole(role.Role_Type__c),
        });
      }
    });
  }

  // Add jury portal if accessible AND no Panelist role exists (to avoid duplication)
  if (apiData.jury && apiData.jury.access && !hasPanelistRole) {
    transformed.push({
      id: apiData.jury.userId,
      name: "JuryPortal",
      label: "Jury Portal",
      roleType: null,
      img: jury,
    });
  }

  // Add venue-booking portal if accessible
  if (apiData["venue-booking"] && apiData["venue-booking"].access) {
    transformed.push({
      id: apiData["venue-booking"].User.id,
      name: "VenuePortal",
      label: "Venue Portal",
      roleType: apiData["venue-booking"].roleType || null,
      img: venue,
    });
  }

  return transformed;
};

const Preference = () => {
  const [loadingCount, setLoadingCount] = useState<number>(0);
  const [selectedPreference, setSelectedPreference] = useState<string | null>(null);
  const navigate = useHistory(); // For navigation
  const isLoading = loadingCount > 0;
  const [portaldata, setportaldata] = useState<PortalData[]>([]);
  
  // Add theme and media query for mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const loadPortalData = async () => {
      const storedData = await storageService.getItem("portaldata");
      if (storedData) {
        const parsedData: PortalApiResponse = typeof storedData === 'string' ? JSON.parse(storedData) : storedData;
        const transformedData = transformData(parsedData);
        console.log(transformedData, "transformedDatatransformedDatatransformedData");

        setportaldata(transformedData);
        
        // Load previously selected preference
        const previouslySelectedPreference = await storageService.getItem('preferences');
        if (previouslySelectedPreference) {
          const selectedName = Array.isArray(previouslySelectedPreference)
            ? previouslySelectedPreference[0]
            : JSON.parse(previouslySelectedPreference)[0];
          const selectedItem = transformedData.find((item) => item.name === selectedName);
          setSelectedPreference(selectedItem ? selectedItem.id : null);
        } else {
          setSelectedPreference(null);
        }
      }
    };
    
    loadPortalData();
  }, []);

  // Handle radio selection
  const handleSelect = (id: string) => {
    setSelectedPreference(id);
  };

  // Handle navigation to the dashboard
  const handleGoToDashboard = async () => {
    // Save only the selected name
    const selectedItem = portaldata.find((item) => item.id === selectedPreference);
    const selectedNames = selectedItem ? [selectedItem.name] : [];
    await storageService.setItem("preferences", selectedNames);
    
    // Check if user is Guest and redirect accordingly
    const isGuestUser = selectedNames[0] === 'Guest';
    if (isGuestUser) {
      navigate.push(routes.facilities);
    } else {
      navigate.push(routes.home);
    }
  };

  return (
    <>
      {isLoading ? (
        <KenLoader />
      ) : (
        <>
          {isMobile ? (
            // Mobile View
            <Box
              sx={{
                backgroundColor: "#ffffff",
                minHeight: "100vh",
                padding: "20px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Header */}
              <Box sx={{ marginBottom: "32px" }}>
                <Typography 
                  sx={{ 
                    fontFamily: 'General Sans',
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#111827',
                    marginBottom: '8px'
                  }}
                >
                  Welcome
                </Typography>
                <Typography 
                  sx={{ 
                    fontFamily: 'General Sans',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#6B7280',
                    lineHeight: '140%'
                  }}
                >
                  Kindly select all the preference to customize your experience.
                </Typography>
              </Box>

              {/* Preferences List */}
              <Box sx={{ flex: 1 }}>
                {portaldata.map((item) => (
                  <Box
                    key={item.id}
                    sx={{
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      padding: '16px',
                      marginBottom: '12px',
                      backgroundColor: '#ffffff',
                    }}
                  >
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Radio
                            checked={selectedPreference === item.id}
                            onChange={() => handleSelect(item.id)}
                            sx={{
                              color: '#D1D5DB',
                              '&.Mui-checked': {
                                color: '#604797',
                              },
                            }}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              fontFamily: 'General Sans',
                              fontSize: '16px',
                              fontWeight: 400,
                              color: '#111827',
                            }}
                          >
                            {item.label}
                          </Typography>
                        }
                      />
                    </FormGroup>
                  </Box>
                ))}
              </Box>

              {/* Next Button */}
              <Box sx={{ marginTop: 'auto', paddingTop: '20px' }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleGoToDashboard}
                  sx={{
                    height: '48px',
                    fontFamily: 'General Sans',
                    fontSize: '16px',
                    fontWeight: 500,
                    lineHeight: '140%',
                    letterSpacing: '0%',
                    textTransform: 'none',
                    background: 'linear-gradient(90.07deg, #604797 0.05%, #7A377E 99.94%)',
                    borderRadius: '8px',
                    '&:hover': {
                      background: 'linear-gradient(90.07deg, #5a4088 0.05%, #6b3070 99.94%)',
                    },
                    '&:disabled': {
                      backgroundColor: '#D1D5DB',
                      color: '#9CA3AF',
                    },
                  }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          ) : (
            // Desktop View (Original)
            <Grid
              style={{
                backgroundColor: "#f7f8fb",
                height: "100vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box
                style={{
                  backgroundColor: "white",
                  width: "calc(100% - 30%)",
                  height: "calc(100% - 15%)",
                  padding: "60px",
                  boxSizing: "border-box",
                  border: "1px solid #E6E6E6",
                  borderRadius: "8px",
                }}
              >
                <Grid>
                  <Typography fontWeight={600} fontSize={16}>
                    Welcome
                  </Typography>
                  <Typography fontSize={14} mt={1}>
                    Kindly select all the preferences to customize your experience.
                  </Typography>
                  <Grid mt={1} container>
                    {portaldata.map((item) => (
                      <Grid
                        key={item.id}
                        lg={4}
                        style={{
                          backgroundImage: `url(${item?.img})`,
                          height: 230,
                          backgroundRepeat: "no-repeat",
                          backgroundSize: "cover",
                          borderRadius: 8,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "flex-end",
                          margin: 10
                        }}
                      >
                        <Grid
                          style={{
                            backgroundColor: "white",
                            borderRadius: 5,
                            margin: 10,
                            paddingLeft: 15,
                          }}
                        >
                            <FormGroup>
                            <FormControlLabel
                              control={
                              <Radio
                                checked={selectedPreference === item.id}
                                onChange={() => handleSelect(item.id)}
                              />
                              }
                              label={item.label === "Startup_Team_Member" ? "Startup Member" : item.label}
                            />
                            </FormGroup>
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>
                  <Grid container justifyContent="center" mt={3}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleGoToDashboard}
                    >
                      Go to Dashboard
                    </Button>
                  </Grid>
                </Grid>
              </Box >
            </Grid >
          )}
        </>
      )}
    </>
  );
};

export default Preference;
